import { hasRegisterCessationDate } from './mapContactToRegisterCommon';

import type { MyRegisterOfficerUpdatePayload } from './myRegisterOfficerOccupation';

export type MyRegisterOfficerDatesPayload = MyRegisterOfficerUpdatePayload;

export type MyRegisterOfficerSaveSnapshot = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
    occupation?: string;
    /** 멤버 법인 지배 주체(AR Section F) — 저장 실패 롤백 시 되돌릴 값 (멤버 저장에서만 사용) */
    controlledBy?: string;
};

async function rollbackOfficerDates(
    updateOfficer: (
        entityCd: string,
        officerIdx: number,
        payload: MyRegisterOfficerDatesPayload,
    ) => Promise<void>,
    entityCd: string,
    officerIdx: number,
    appointmentDate: string | null,
    cessationDate: string | null,
    occupation: string | undefined,
    primaryError: unknown,
): Promise<never> {
    try {
        await updateOfficer(entityCd, officerIdx, {
            appointmentDate,
            cessationDate,
            ...(occupation !== undefined ? { occupation } : {}),
        });
    } catch (rollbackErr) {
        const primary = primaryError instanceof Error ? primaryError.message : String(primaryError);
        const rollback = rollbackErr instanceof Error ? rollbackErr.message : String(rollbackErr);
        throw new Error(
            `${primary} (officer date rollback also failed: ${rollback})`,
        );
    }
    throw primaryError;
}

/**
 * MY Register Officer 저장 — Officer 선저장 후 (재직 시에만) Contact·profile history.
 * 퇴임일이 있으면 L_OFFICER/L_BO 등 역할 메타만 저장하고 live Global Contact는 갱신하지 않음.
 * profile history sync는 live 스냅샷 기준(요청 body의 인적사항으로 live를 덮지 않음).
 */
export async function runMyRegisterOfficerSaveWithProfileHistory(opts: {
    entityCd: string;
    officerIdx: number;
    appointmentDate: string | null;
    cessationDate: string | null;
    officerDatesSnapshot?: MyRegisterOfficerSaveSnapshot | null;
    officerOccupation?: string;
    officerUpdatePayload?: MyRegisterOfficerUpdatePayload;
    updateOfficer: (
        entityCd: string,
        officerIdx: number,
        payload: MyRegisterOfficerDatesPayload,
    ) => Promise<void>;
    updateContact: () => Promise<unknown>;
    /** 해임(cessation)이어도 updateContact(reg no·주소)를 실행할지 — 예: Auditor */
    alsoUpdateContactWhenCeased?: boolean;
    registerProfile?: Record<string, unknown> | null;
    syncProfileHistory?: (
        entityCd: string,
        officerIdx: number,
        body: {
            cessationDate?: string | null;
            registerProfile?: Record<string, unknown>;
        },
    ) => Promise<unknown>;
}): Promise<void> {
    const {
        entityCd,
        officerIdx,
        appointmentDate,
        cessationDate,
        officerDatesSnapshot,
        officerOccupation,
        officerUpdatePayload,
        updateOfficer,
        updateContact,
        alsoUpdateContactWhenCeased,
        syncProfileHistory,
        registerProfile,
    } = opts;

    const rollbackAppointment =
        officerDatesSnapshot?.appointmentDate !== undefined
            ? officerDatesSnapshot.appointmentDate
            : appointmentDate;
    const rollbackCessation =
        officerDatesSnapshot?.cessationDate !== undefined
            ? officerDatesSnapshot.cessationDate
            : null;
    const rollbackOccupation =
        officerDatesSnapshot?.occupation !== undefined
            ? officerDatesSnapshot.occupation
            : undefined;

    const officerPutPayload: MyRegisterOfficerDatesPayload = officerUpdatePayload
        ? {
            ...officerUpdatePayload,
            appointmentDate,
            cessationDate,
        }
        : {
            appointmentDate,
            cessationDate,
            ...(officerOccupation !== undefined ? { occupation: officerOccupation } : {}),
        };

    await updateOfficer(entityCd, officerIdx, officerPutPayload);

    if (hasRegisterCessationDate(cessationDate)) {
        if (syncProfileHistory) {
            try {
                await syncProfileHistory(entityCd, officerIdx, {
                    cessationDate,
                    ...(registerProfile ? { registerProfile } : {}),
                });
            } catch (historyErr) {
                await rollbackOfficerDates(
                    updateOfficer,
                    entityCd,
                    officerIdx,
                    rollbackAppointment,
                    rollbackCessation,
                    rollbackOccupation,
                    historyErr,
                );
            }
        }
        // 해임이어도 Contact(reg no)·주소를 live(L_GLOBAL_CONTACT/L_CONTACT_BRANCH)에 반영 (Auditor 등 opt-in)
        if (alsoUpdateContactWhenCeased) {
            try {
                await updateContact();
            } catch (contactError) {
                await rollbackOfficerDates(
                    updateOfficer,
                    entityCd,
                    officerIdx,
                    rollbackAppointment,
                    rollbackCessation,
                    rollbackOccupation,
                    contactError,
                );
            }
        }
        return;
    }

    try {
        await updateContact();
    } catch (contactError) {
        await rollbackOfficerDates(
            updateOfficer,
            entityCd,
            officerIdx,
            rollbackAppointment,
            rollbackCessation,
            rollbackOccupation,
            contactError,
        );
    }
}
